/* eslint-disable max-len */
import React from 'react';
import { connect } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';

import Bookings from 'assets/images/reports/Bookings.svg';
import Clients from 'assets/images/reports/Clients.svg';
import Commissions from 'assets/images/reports/Commissions.svg';
import Discounts from 'assets/images/reports/Discounts.svg';
import Downloadable from 'assets/images/reports/Downloadable.svg';
import FutureRevenue from 'assets/images/reports/FutureRevenue.svg';
import Hours from 'assets/images/reports/Hours.svg';
import MonthlyDashboard from 'assets/images/reports/MonthlyDashboard.svg';
import PaymentTypes from 'assets/images/reports/PaymentTypes.svg';
import Retail from 'assets/images/reports/Retail.svg';
import Revenue from 'assets/images/reports/Revenue.svg';
import StaffKPIs from 'assets/images/reports/StaffKPIs.svg';
import WeeklyDashboard from 'assets/images/reports/WeeklyDashboard.svg';
import CurrencySign from 'components/price/CurrencySign.component';
import LINK from 'constants/link.const';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import linkTo from 'utils/links/linkTo.util';

import { setCurrentTable } from 'reports/actions';

const TooltipDiscount = () => (
  <>
    Discount report will only show <CurrencySign /> discount applied at check out. Booking are excluded.
  </>
);
const TooltipPayment = () => (
  <>
    Useful to help answer any questions in cash up e.g where is the missing <CurrencySign />
    50 cash
  </>
);

const REPORTS = [
  {
    id: '1',
    name: 'Weekly Dashboard',
    description:
      'Health check for your business over any one week period: forecast revenue vs actual, average bill & rebooking rate. Based on checked out appointments.',
    img: WeeklyDashboard,
    tooltip:
      'More detail or staff breakdown can be found in Revenue reports. Comparative charts over the year are in Monthly Dashboard',
    colorBtn: '#821bb8',
    reportToGo: 'dashboard',
  },
  {
    id: '2',
    name: 'Staff KPIs',
    description:
      'Review each staff member with key performance indicators like booking value, occupancy, average bill, rebook rate & client requests. Can be set for week or month.',
    img: StaffKPIs,
    tooltip:
      'These metrics are based off value of appointments in your Slick diary. For actual revenue based on client bills taken, see Revenue reports',
    colorBtn: '#821bb8',
    reportToGo: 'staffkpi',
  },
  {
    id: '3',
    name: 'Monthly Dashboard',
    description:
      'Health check for your business by month: see your progress over the year and compare revenue vs last year',
    img: MonthlyDashboard,
    tooltip: ' If you would like more detailed reports or breakdown by staff then check out the Revenue reports',
    colorBtn: '#821bb8',
    reportToGo: 'monthly-dashboard',
  },
  {
    id: '4',
    name: 'Revenue',
    description:
      'Business revenue broken down by staff member and category over any time period with option to exclude VAT',
    img: Revenue,
    tooltip: 'Need to calculate staff commissions? Check out our Commission report',
    colorBtn: '#5e72d1',
    reportToGo: 'revenue',
  },
  {
    id: '5',
    name: 'Future revenue',
    description:
      'Check estimated future revenue and occupancy for your business and each staff member based on appointments you already have in the diary',
    img: FutureRevenue,
    tooltip: 'To use this report you will need to select a future date range via the date pickers',
    colorBtn: '#5e72d1',

    reportToGo: 'future-revenue',
  },
  {
    id: '6',
    name: 'Discounts',
    description:
      'Useful to track any discounts applied at checkout. See breakdown by staff member and discounts given by service category or retail',
    img: Discounts,
    tooltip: <TooltipDiscount />,
    colorBtn: '#5e72d1',
    reportToGo: 'revenue',
  },
  {
    id: '7',
    name: 'Retail',
    description:
      'Report showing volume and value of products sold per staff member. Useful for analysing how good your team are at selling retail',
    img: Retail,
    tooltip: 'Retail report requires products to be checked out',
    colorBtn: '#e65e9e', // pink
    reportToGo: 'retail',
  },
  {
    id: '8',
    name: 'Staff Commissions',
    description: 'Perfect for paying wages. Allows you to set % commission rates per staff and per category.',
    img: Commissions,
    tooltip: "Don't forget to set a tax rate to calculate commissions net of tax",
    colorBtn: '#e65e9e', // pink
    reportToGo: 'commissions',
  },
  {
    id: '9',
    name: 'Payment Types',
    description:
      'Need to see takings by stylist broken down by payment type? Click here to find cash/card splits at stylist & client level.',
    img: PaymentTypes,
    tooltip: <TooltipPayment />,
    colorBtn: '#e65e9e',
    reportToGo: 'payments',
  },
  {
    id: '10',
    name: 'Hours',
    description: 'Paying wages based on an hourly rate? Check hours busy and time off here.',
    img: Hours,
    tooltip: 'Use this to see staff occupancy i,e what % of their time in salon are they with a client',
    colorBtn: '#3da49d',
    reportToGo: 'hours',
  },
  {
    id: '11',
    name: 'Clients',
    description:
      'Metrics to help track & improve client satisfaction: how many clients rebook, how often do they visit, which staff members are clients requesting?',
    img: Clients,
    tooltip:
      'Rebooking is calculated as a client that books another appointment within 24 hours of their last appointment',
    colorBtn: '#3da49d', // green
    reportToGo: 'staffkpi',
  },
  {
    id: '12',
    name: 'Bookings',
    description:
      'See how many bookings were created over a certain date range and whether they were made online or in-salon.',
    img: Bookings,
    tooltip: 'Use bookings by status to see if any appointments weren’t checked out',
    colorBtn: '#3da49d',
    reportToGo: 'bookings',
  },
  {
    id: '13',
    name: 'Downloadable',
    description: 'Is your accountant asking for a list of all transactions? Do you need to see deleted bookings?',
    img: Downloadable,
    tooltip: 'Useful if you want to run / analyse your own reports in Excel',
    colorBtn: '#e2c67a', // ocher
    reportToGo: 'downloadable',
  },
];

const BucketsReportsLayout = ({ setCurrentTable }) => {
  const history = useHistory();
  const salonId = useCurrentSalonId();
  const summaryKpiLink = linkTo({ pattern: LINK.slnStaffReportsKpi, params: { salonId }, fault: '#' });

  const gotoReport = (url, tableName) => {
    const toPath = `/salon/${salonId}/reports/${url}`;
    history.push(toPath);
    setCurrentTable(tableName);
  };

  return (
    <div className="bucket-layout">
      <h2>How can Slick help analyse your business today?</h2>
      <div className="buckets-container">
        {REPORTS.map((report) => (
          <div className="list-grid-item" key={report.id}>
            <div className="bucket-text">
              <div className="primary-text">{report.name}</div>
              <div className="supporting-text">{report.description}</div>
            </div>
            <div className="rectangle">
              <img className="img_test" src={report.img} alt={report.name} />
              <div className="img-shadow" />
            </div>
            <div className="client-info">
              <div className="info-icon">
                <span className="tooltip">
                  <div className="tooltip-centered">{report.tooltip}</div>
                </span>
              </div>
              <span className="client-info-span">How should I use this report?</span>
            </div>
            <div className="d-block">
              {report.id === '2' || report.id === '11' ? (
                <NavLink to={summaryKpiLink} className="btn rainbow" style={{ backgroundColor: report.colorBtn }}>
                  Go to report
                </NavLink>
              ) : (
                <button
                  type="button"
                  className="btn rainbow"
                  style={{ backgroundColor: report.colorBtn }}
                  onClick={() => gotoReport(report.reportToGo, report.name)}
                >
                  Go to report
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  setCurrentTable: (table) => dispatch(setCurrentTable(table)),
});

export default connect(null, mapDispatchToProps)(BucketsReportsLayout);
